import { EventData } from '@aether/services/sanity-service'
import { styled } from '@aether/styles'
import { FC, PropsWithChildren, useState } from 'react'
import { Link } from '../Link/Link'
import { Media } from '../Media/Media'
import { useTranslation } from 'react-i18next'
import { EventButton } from '../EventButton/EventButton'
import { useCustomerContext } from '@aether/account/utils-customer-context'
import { getIsDateInPDTInPast } from '@aether/utils'

const Root = styled('article', {
  display: 'grid',
  transition: 'background-color 0.5s ease',
  border: '1px solid $gray_light_third',
  borderRadius: '$r4',
  position: 'relative',
  overflow: 'hidden',

  '&:hover': {
    backgroundColor: '$gray_light_third',
  },

  variants: {
    isArchived: {
      true: {
        backgroundColor: '$gray_light',
      },
    },
    isLinkDisabled: {
      true: {
        '&:hover': {
          backgroundColor: 'inherit',
        },
        cursor: 'default',
      },
    },
  },
})

const CardContent = styled('div', {
  position: 'relative',
  display: 'grid',
  width: '100%',
  gridColumn: 1,
  gridRow: 1,
  pointerEvents: 'none',
  padding: '$20',

  variants: {
    isArchived: {
      true: {
        '@lg': {
          padding: '$28',
        },
      },
      false: {
        '@lg': {
          padding: '$40',
        },
      },
    },
  },
  defaultVariants: {
    isArchived: false,
  },
})

const DetailsWrap = styled('div', {
  display: 'grid',
  gridTemplateRows: 'auto auto',
  gap: '$10',
  marginBottom: '$56',

  '@lg': {
    marginBottom: '$40',
  },
})

const Title = styled('h2', {
  $aetherFont: 'heading04',
  marginBottom: '$8',

  variants: {
    isArchived: {
      true: {
        '@lg': {
          $aetherFont: 'heading05',
        },
      },
    },
  },
})

const Actions = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '$10',
  alignItems: 'center',
})

const MediaWrap = styled('div', {
  marginBottom: '$28',
  position: 'relative',

  '&::after': {
    content: '',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(0deg, $black_light, $black_light)',
    opacity: 0,
  },

  variants: {
    isArchived: {
      true: {
        '&::after': {
          opacity: 1,
        },
        '& img': {
          filter: 'grayscale(100%)',
        },
      },
    },
  },
})

const Details = styled('div', {
  display: 'grid',
  gridTemplateRows: 'auto auto',
  gap: '$8',
  $aetherFont: 'ui06',
  fontSize: 12,
})

const Status = styled('span', {
  $aetherFont: 'ui06',
  fontSize: 12,

  variants: {
    isVipEvent: {
      true: {
        color: '$blue',
      },
    },
  },
})

const ButtonsWrap = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '$10',
  alignItems: 'center',
  position: 'relative',
  zIndex: 1,
  pointerEvents: 'auto',

  '@lg': {
    gap: '$12',
  },
})

const ResponsiveText = styled('span', {
  variants: {
    variant: {
      mobile: {
        display: 'inline',
        '@lg': {
          display: 'none',
        },
      },
      desktop: {
        display: 'none',
        '@lg': {
          display: 'inline',
        },
      },
    },
  },
})

const LinkWrap = styled('div', {
  display: 'grid',
  gridColumn: 1,
  gridRow: 1,
  '& > a': {
    pointerEvents: 'auto',
  },
})

export const formatDateLong = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export const EventCard: FC<
  PropsWithChildren<EventData & { isArchived?: boolean }>
> = ({
  title,
  startDate,
  rsvpEndDate,
  endDate,
  locationShort,
  media,
  handle,
  handleOverrideLink,
  isArchived,
  primaryCta,
  isVipEvent,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const isRspvAction = primaryCta?.link?.action?.type === 'rsvpModalLinkAction'
  const { t } = useTranslation('eventCard')
  const { userRole } = useCustomerContext()
  const isVip = userRole === 'vip'
  const isRsvpValid = !getIsDateInPDTInPast(rsvpEndDate)
  const isShowRsvpButton =
    (!isVipEvent && isRspvAction && isRsvpValid) ||
    (isVipEvent && isVip) ||
    !isArchived
  const generateDate = (startDate: string, endDate?: string) => {
    if (startDate && endDate) {
      return `${formatDateLong(startDate)} - ${formatDateLong(endDate)}`
    }
    return formatDateLong(startDate)
  }

  const vipAdjustedHandle = isVipEvent ? `/vip${handle}` : handle
  const isLinkDisabled = isVipEvent && !isVip && !!userRole
  const linkProps = handleOverrideLink || {
    href: vipAdjustedHandle,
    disabled: isLinkDisabled,
    onClick: (e: React.MouseEvent | React.KeyboardEvent) => {
      if (isLinkDisabled) {
        e.preventDefault()
      }
    },
  }

  const handleMouseEnter = () => {
    if (isLinkDisabled) {
      return
    }
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    if (isLinkDisabled) {
      return
    }
    setIsHovered(false)
  }

  return (
    <Root
      isArchived={isArchived}
      isLinkDisabled={isLinkDisabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <CardContent isArchived={isArchived}>
        <MediaWrap isArchived={isArchived}>
          <Media
            mediaPayload={media?.mediaPayload}
            hardcropRatio="landscape1"
          />
        </MediaWrap>
        <Link {...linkProps}>
          <Title isArchived={isArchived}>{title}</Title>
        </Link>
        <DetailsWrap>
          <Details>
            <span>
              {startDate && (
                <>
                  {t('date')}: {generateDate(startDate, endDate)}
                </>
              )}
            </span>
            <span>
              {locationShort && (
                <>
                  {t('location')}: {locationShort}
                </>
              )}
            </span>
          </Details>
        </DetailsWrap>
        <Actions>
          <ButtonsWrap>
            <Link active={isHovered} appearance="primary" {...linkProps}>
              <ResponsiveText variant="mobile">{t('details')}</ResponsiveText>
              <ResponsiveText variant="desktop">
                {t('eventDetails')}
              </ResponsiveText>
            </Link>
            {isShowRsvpButton && (
              <div onMouseEnter={() => setIsHovered(false)}>
                <EventButton
                  eventData={{
                    isVipEvent: isVipEvent ?? false,
                    isRsvpEnded: !isRsvpValid,
                    primaryCta,
                  }}
                  isVip={isVip}
                  isCardVariant
                  overrideLabel={t('rsvp') as string}
                />
              </div>
            )}
          </ButtonsWrap>

          <Status isVipEvent={isVipEvent}>
            {isArchived ? (
              t('archived')
            ) : isVipEvent ? (
              <>
                <ResponsiveText variant="mobile">
                  {t('aetherInsiderEventShort')}
                </ResponsiveText>
                <ResponsiveText variant="desktop">
                  {t('aetherInsiderEvent')}
                </ResponsiveText>
              </>
            ) : (
              t('openEvent')
            )}
          </Status>
        </Actions>
      </CardContent>
      <LinkWrap>
        <Link {...linkProps} tabIndex={-1} aria-hidden={true} />
      </LinkWrap>
    </Root>
  )
}
